import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import FindParcelService from './FindParcelService';
import OrderByOptionService from '../../setting/orderByOption/OrderByOptionService';
import { formatDate } from '../../../utils/dateUtils';
import useShipDate from '../../setting/shipDate/useShipDate';

const SHOPS = ['smile', 'tama'];

const defaultOrderDateFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 60);
  return d.toISOString().slice(0, 10);
};

const initialFilters = () => ({
  orderDateFrom: defaultOrderDateFrom(),
  orderDateTo: '',
  shipDateIds: [],
  noShipDate: false,
  shop: '',
  nameSearch: '',
  trackingJp: '',
  onlyNoTracking: false,
  isHandCarry: '',
  orderByOptionId: '',
  postcode: '',
});

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const buildPageList = (current, total) => {
  if (total <= 1) return [1];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([
    1, 2, 3,
    total - 2, total - 1, total,
    current - 1, current, current + 1,
  ]);
  const sorted = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  sorted.forEach((p) => {
    if (p - prev > 1) out.push('...');
    out.push(p);
    prev = p;
  });
  return out;
};

const buildParams = (filters, page, limit) => {
  const p = { page, limit };
  Object.entries(filters).forEach(([k, v]) => {
    if (v === '' || v === false || v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      p[k] = v.join(',');
      return;
    }
    p[k] = v === true ? 'true' : v;
  });
  return p;
};

const FindParcel = () => {
  const { shipDates, getShipDates } = useShipDate({ manage: 'auto' });
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imageSize, setImageSize] = useState(120);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [orderByOptions, setOrderByOptions] = useState([]);

  const load = useCallback(async (filtersToUse, pageToUse) => {
    setLoading(true);
    try {
      const res = await FindParcelService.search(buildParams(filtersToUse, pageToUse, limit));
      if (res.postcodeResolved === false) {
        toast.warn('ไม่พบจังหวัดจากรหัสไปรษณีย์นี้ กรุณาตรวจสอบรหัส');
        setRows([]);
        setTotal(0);
        setTotalPages(0);
        return;
      }
      setRows(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 0);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 502 && e.response?.data?.error === 'postcode_service') {
        toast.error('บริการค้นหารหัสไปรษณีย์ขัดข้อง กรุณาลองใหม่');
      } else {
        toast.error('โหลดข้อมูลไม่สำเร็จ');
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load(appliedFilters, page);
  }, [load, appliedFilters, page]);

  useEffect(() => {
    getShipDates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    OrderByOptionService.getActiveOrderByOptions()
      .then((res) => setOrderByOptions(res.data || []))
      .catch(() => setOrderByOptions([]));
  }, []);

  const visibleShipDates = (shipDates || []).filter((d) => {
    if (filters.isHandCarry === 'true') return d.isAir === true;
    if (filters.isHandCarry === 'false') return d.isAir === false;
    return true;
  });

  const [shipDateOpen, setShipDateOpen] = useState(false);
  const shipDateRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (shipDateRef.current && !shipDateRef.current.contains(e.target)) {
        setShipDateOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggleShipDateId = (id) => {
    setFilters((prev) => {
      const set = new Set(prev.shipDateIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, shipDateIds: [...set] };
    });
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onSearch = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const onReset = () => {
    const fresh = initialFilters();
    setFilters(fresh);
    setPage(1);
    setAppliedFilters(fresh);
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">ตามหาพัสดุ</h1>
        <span className="text-sm text-gray-500">
          พบ {total.toLocaleString()} รายการ
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">วันที่สั่ง (จาก)</label>
            <input
              type="date"
              value={filters.orderDateFrom}
              onChange={(e) => updateFilter('orderDateFrom', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">วันที่สั่ง (ถึง)</label>
            <input
              type="date"
              value={filters.orderDateTo}
              onChange={(e) => updateFilter('orderDateTo', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-4 pt-5">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.onlyNoTracking}
                onChange={(e) => updateFilter('onlyNoTracking', e.target.checked)}
                className="h-4 w-4"
              />
              เฉพาะไม่มีเลขพัสดุ
            </label>
          </div>
          <div className="pt-5">
            <label className="block text-xs font-medium text-gray-600 mb-1">ช่องทาง</label>
            <select
              value={filters.isHandCarry}
              onChange={(e) => updateFilter('isHandCarry', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            >
              <option value="">ทั้งหมด</option>
              <option value="false">เรือ</option>
              <option value="true">แอร์</option>
            </select>
          </div>
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100">
            <div ref={shipDateRef} className="relative">
              <label className="block text-xs font-medium text-gray-600 mb-1">รอบเรือ / รอบบิน</label>
              <button
                type="button"
                onClick={() => setShipDateOpen((v) => !v)}
                className="w-full flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 text-sm bg-white hover:bg-gray-50"
              >
                <span className="truncate">
                  {filters.shipDateIds.length === 0 && !filters.noShipDate
                    ? 'ทั้งหมด'
                    : `เลือก ${filters.shipDateIds.length + (filters.noShipDate ? 1 : 0)} รอบ`}
                </span>
                <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
              </button>
              {shipDateOpen && (
                <div className="absolute z-20 mt-1 w-64 max-h-72 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg">
                  <div className="flex items-center justify-between px-2 py-1 border-b border-gray-100 bg-gray-50 text-xs">
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={() => setFilters((prev) => ({ ...prev, shipDateIds: [], noShipDate: false }))}
                    >ล้าง</button>
                    <span className="text-gray-500">{filters.shipDateIds.length} / {visibleShipDates.length}</span>
                  </div>
                  <label className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={filters.noShipDate}
                      onChange={() => updateFilter('noShipDate', !filters.noShipDate)}
                      className="h-4 w-4"
                    />
                    <span className="flex-1 text-gray-700">ยังไม่มีรอบ</span>
                  </label>
                  {visibleShipDates.length === 0 && (
                    <div className="px-2 py-3 text-xs text-gray-400 text-center">ไม่มีรอบ</div>
                  )}
                  {visibleShipDates.map((d) => (
                    <label
                      key={d.uuid || d.id}
                      className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.shipDateIds.includes(d.id)}
                        onChange={() => toggleShipDateId(d.id)}
                        className="h-4 w-4"
                      />
                      <span className="flex-1">
                        {formatDate(d.shipDate)}
                        {d.isAir && d.warehouseReceivingAddress?.addressNickName && (
                          <span className="text-gray-500 ml-1 text-xs">[{d.warehouseReceivingAddress.addressNickName}]</span>
                        )}
                      </span>
                      <span className={`text-xs px-1.5 rounded ${d.isAir ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>
                        {d.isAir ? 'แอร์' : 'เรือ'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Shop</label>
              <select
                value={filters.shop}
                onChange={(e) => updateFilter('shop', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="">ทั้งหมด</option>
                {SHOPS.map((s) => (
                  <option key={s} value={s}>{s.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">สั่งซื้อโดย</label>
              <select
                value={filters.orderByOptionId}
                onChange={(e) => updateFilter('orderByOptionId', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="">ทั้งหมด</option>
                {orderByOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">ค้นหาจากชื่อ หรือ ชื่อหน้ากล่อง</label>
              <input
                type="text"
                value={filters.nameSearch}
                onChange={(e) => updateFilter('nameSearch', e.target.value)}
                placeholder="ชื่อลูกค้า / ชื่อหน้ากล่อง"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">รหัสไปรษณีย์ผู้ส่ง (ญี่ปุ่น)</label>
              <input
                type="text"
                value={filters.postcode}
                onChange={(e) => updateFilter('postcode', e.target.value)}
                placeholder="เช่น 100-0001"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">เลขพัสดุญี่ปุ่น (บางส่วน)</label>
              <input
                type="text"
                value={filters.trackingJp}
                onChange={(e) => updateFilter('trackingJp', e.target.value)}
                placeholder="เช่น 1234..."
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            {showAdvanced ? (
              <>
                <ChevronUpIcon className="h-4 w-4" /> ซ่อนตัวกรองเพิ่มเติม
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" /> ตัวกรองเพิ่มเติม
              </>
            )}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onReset}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50"
            >
              ล้าง
            </button>
            <button
              type="button"
              onClick={onSearch}
              className="inline-flex items-center gap-1 px-4 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              <MagnifyingGlassIcon className="h-4 w-4" /> ค้นหา
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-2 px-1">
        <span className="text-xs text-gray-600">ขนาดรูป</span>
        <input
          type="range"
          min={60}
          max={480}
          step={10}
          value={imageSize}
          onChange={(e) => setImageSize(parseInt(e.target.value, 10))}
          className="w-48"
        />
        <span className="text-xs text-gray-500">{imageSize}px</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
              <tr>
                <th className="px-3 py-2">รูป</th>
                <th className="px-3 py-2">หลักฐานซื้อ</th>
                <th className="px-3 py-2">Shop</th>
                <th className="px-3 py-2">ลูกค้า</th>
                <th className="px-3 py-2">Smile Name</th>
                <th className="px-3 py-2">Tracking JP</th>
                <th className="px-3 py-2 text-right">จำนวน</th>
                <th className="px-3 py-2 text-right">ราคา</th>
                <th className="px-3 py-2">ช่องทาง</th>
                <th className="px-3 py-2">วันสั่ง</th>
                <th className="px-3 py-2">วันส่ง</th>
                <th className="px-3 py-2">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={12} className="px-3 py-6 text-center text-gray-500">กำลังโหลด...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-3 py-6 text-center text-gray-400">ไม่พบข้อมูล</td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 align-middle">
                  <td className="px-3 py-2">
                    {r.imageUrl ? (
                      <img
                        src={r.imageUrl}
                        alt=""
                        style={{ width: imageSize, height: imageSize, objectFit: 'cover' }}
                        onClick={() => setZoomImage(r.imageUrl)}
                        className="rounded border border-gray-200 cursor-zoom-in"
                      />
                    ) : (
                      <div
                        style={{ width: imageSize, height: imageSize }}
                        className="rounded border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-300 text-xs"
                      >
                        ไม่มีรูป
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.purchaseConfirmUrl ? (
                      <a
                        href={r.purchaseConfirmUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-xs"
                      >เปิดรูป</a>
                    ) : (
                      <span className="text-gray-400 text-xs">ไม่มีรูป</span>
                    )}
                  </td>
                  <td className="px-3 py-2 uppercase">{r.shop || '-'}</td>
                  <td className="px-3 py-2">
                    {r.customer?.contact ? (
                      r.customer?.smileName ? (
                        <a
                          href={`/customer-profile?smileName=${encodeURIComponent(r.customer.smileName)}${r.shop ? `&shop=${encodeURIComponent(r.shop)}` : ''}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >{r.customer.contact}</a>
                      ) : (
                        r.customer.contact
                      )
                    ) : '-'}
                  </td>
                  <td className="px-3 py-2">{r.smileNameInput || '-'}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.trackingJp || <span className="text-rose-500">ไม่มีเลข</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{r.qty}</td>
                  <td className="px-3 py-2 text-right">{Number(r.amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    {r.isHandCarry ? (
                      <span className="inline-flex items-center rounded bg-sky-100 text-sky-800 px-2 py-0.5 text-xs">แอร์</span>
                    ) : (
                      <span className="inline-flex items-center rounded bg-slate-100 text-slate-700 px-2 py-0.5 text-xs">เรือ</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.orderDate ? formatDate(r.orderDate) : '-'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.shipDate ? formatDate(r.shipDate) : '-'}</td>
                  <td className="px-3 py-2">
                    {r.url ? (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline text-xs"
                      >เปิดลิงก์</a>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t border-gray-100 bg-gray-50 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <span>หน้า {page} / {totalPages || 1}</span>
            <span className="text-gray-300">|</span>
            <label className="inline-flex items-center gap-1">
              ต่อหน้า
              <select
                value={limit}
                onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }}
                className="border border-gray-300 rounded px-1 py-0.5 text-sm bg-white"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage(1)}
              className="px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >«</button>
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >‹</button>
            {buildPageList(page, totalPages || 1).map((p, i) =>
              p === '...' ? (
                <span key={`e${i}`} className="px-2 text-gray-400 select-none">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  disabled={loading}
                  onClick={() => setPage(p)}
                  className={`min-w-[2rem] px-2 py-1 rounded border text-sm ${
                    p === page
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white hover:bg-gray-100'
                  }`}
                >{p}</button>
              )
            )}
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >›</button>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(totalPages)}
              className="px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >»</button>
          </div>
        </div>
      </div>

      {zoomImage && (
        <div
          className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white p-2 rounded hover:bg-white/20"
            onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <img
            src={zoomImage}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default FindParcel;
